import React, { useState } from "react";
import {
  Card,
  Input,
  Slider,
  Select,
  Tabs,
  Button,
  Typography,
  Row,
  Col,
} from "antd";
import { CalculatorOutlined } from "@ant-design/icons";

const { TabPane } = Tabs;
const { Title, Text } = Typography;
const { Option } = Select;
type CalculatorPageProps = {
  companyInfo: any; // Replace 'any' with the actual type if available
};
const CalculatorPage: React.FC<CalculatorPageProps> = ({ companyInfo }) => {
  const [dayEnergyAmps, setDayEnergyAmps] = useState<number | "">("");
  const [nightEnergyAmps, setNightEnergyAmps] = useState<number | "">("");
  const [nightHours, setNightHours] = useState<number>(10); // Duration of usage during the night
  const [batteryType, setBatteryType] = useState<string>("lithium");
  const [systemType, setSystemType] = useState<string>("hybrid"); // Default system type
  const [phaseType, setPhaseType] = useState<string>("single"); // Default phase type
  const [activeTab, setActiveTab] = useState<string>("1"); // Track the active tab

  const [result, setResult] = useState<{
    panelResults: {
      panelName: string;
      panelCount: number;
      panelCost: number;
    }[];
    batteryCount: number;
    batteryCost: number;
    inverterSizeKw: number;
    inverterCost: number;
    totalCost: number;
  } | null>(null);

  // Example panel, battery, and inverter data
  const panels = companyInfo[0]?.panel?.map((panel: any) => ({
    wattage: panel.watt,
    price: panel.price,
    efficiency: panel.efficiency,
    name: panel.name,
    voc: panel.voc,
    isc: panel.isc,
    size: panel.watt,
  }));

  interface Battery {
    batteryName: string;
    batteryPower: number;
    batteryPrice: number;
    betteryAh: number;
    batteryVoltage: number;
    batteryType: string;
    batteryCycle: number;
  }

  const batteries: Battery[] | undefined = companyInfo[0]?.battery?.map(
    (battery: any): Battery => ({
      batteryName: battery.batteryName,
      batteryPower: battery.batteryPower,
      batteryPrice: battery.batteryPrice,
      betteryAh: battery.betteryAh,
      batteryVoltage: battery.batteryVoltage,
      batteryType: battery.batteryType,
      batteryCycle: battery.batteryCycle,
    })
  );

  interface Inverter {
    watt: number;
    name: string;
    price: number;
    efficiency: number;
    maxInputVoltage: number;
    maxInputCurrent: number;
    minInputVoltage: number;
    type?: string;
    phase?: string;
    size?: number;
  }

  const inverters: Inverter[] | undefined = companyInfo[0]?.inverter?.map(
    (inverter: any): Inverter => ({
      watt: inverter.watt,
      name: inverter.name,
      price: inverter.price,
      efficiency: inverter.efficiency,
      maxInputVoltage: inverter.maxInputVoltage,
      maxInputCurrent: inverter.maxInputCurrent,
      minInputVoltage: inverter.minInputVoltage,
      type: inverter.type,
      phase: inverter.phase,
      size: inverter.watt,
    })
  );

  const calculateSystem = () => {
    if (
      typeof dayEnergyAmps !== "number" ||
      dayEnergyAmps <= 0 ||
      typeof nightEnergyAmps !== "number" ||
      nightEnergyAmps <= 0
    ) {
      alert(
        "يرجى إدخال قيم احتياجات الطاقة الصحيحة بالأمبير لكل من فترة النهار وفترة الليل."
      );
      return;
    }

    // Filter inverters based on selected system type
    const filteredInverters = (inverters ?? []).filter(
      (i) => i.type === systemType && i.phase === phaseType
    );

    // Find selected battery
    const battery = batteries?.find((b) => b.batteryType === batteryType);
    if (!battery) {
      alert("يرجى اختيار نوع البطارية");
      return;
    }

    // ✅ Day and Night energy in Wh
    const dayEnergyWh = (dayEnergyAmps * 230) / 1000;
    const nightEnergyWh = nightEnergyAmps * 230 * nightHours;

    // ✅ Calculate results for each panel type
    interface PanelResult {
      panelName: string;
      panelCount: number;
      panelCost: number;
      panelWattage: number;
    }

    interface Panel {
      wattage: number;
      price: number;
      efficiency: number;
      name: string;
      voc: number;
      isc: number;
    }

    const panelResults: PanelResult[] = (panels ?? []).map(
      (panel: Panel): PanelResult => {
        // ✅ Calculate solar panel output per day in kWh
        const panelPowerPerDayKwh: number = (panel.wattage * 0.68) / 1000;

        // ✅ Calculate number of solar panels needed
        const panelCount: number = Math.ceil(dayEnergyWh / panelPowerPerDayKwh);

        // ✅ Panel cost
        const panelCost: number = panelCount * panel.price;

        return {
          panelName: panel.name,
          panelCount,
          panelCost,
          panelWattage: panel.wattage,
        };
      }
    );

    // ✅ Calculate battery requirements
    const nightEnergyKwh = nightEnergyWh / 1000;

    // Battery capacity in kWh
    const batteryCapacityKwh =
      (battery.betteryAh * battery.batteryVoltage * 0.9) / 1000;
    const batteryCount = Math.ceil(nightEnergyKwh / batteryCapacityKwh);
    const batteryCost = batteryCount * battery.batteryPrice;

    // ✅ Inverter selection
    // Calculate inverter for each panel type

    const inverterResults = (panelResults ?? []).map((panelResult, idx) => {
      const panel = panels?.[idx];

      const requiredPowerKw = (panelResult.panelCount * panel?.wattage) / 1000;
      const inverter = filteredInverters.find(
        (i) => typeof i.size === "number" && i.size >= requiredPowerKw
      );

      return {
        inverter,
        inverterSizeKw: inverter?.size || 0,
        inverterCost: inverter?.price || 0,
      };
    });

    // Pick the first inverter result for summary (or you can display all)
    const inverter = inverterResults[0]?.inverter;

    // ✅ Inverter cost
    const inverterCost = inverter?.price || 0;

    // ✅ Total cost
    // Define types for otherCost if not already defined
    interface OtherCost {
      name: string;
      price: number;
    }

    const totalCost: number =
      panelResults.reduce(
        (sum: number, result: { panelCost: number }) => sum + result.panelCost,
        0
      ) +
      batteryCost +
      inverterCost +
      (companyInfo[0]?.otherCost?.value?.reduce(
        (sum: number, result: OtherCost) => sum + result.price,
        0
      ) ?? 0);

    // ✅ Set the result
    setResult({
      panelResults,
      batteryCount,
      batteryCost,
      inverterSizeKw: inverter?.size || 0,
      inverterCost,
      totalCost,
    });
    setActiveTab("2");
  };

  return (
    <div>
      <Card style={{ maxWidth: 800, margin: "20px auto", padding: 20 }}>
        <Title level={3} style={{ textAlign: "center" }}>
          <CalculatorOutlined style={{ marginRight: 8 }} />
          حاسبة نظام الطاقة الشمسية
          <p>
            ملاحظة: يعتمد هذا الحساب على مواصفات ومعدات مكتبنا فقط، و يشمل
            تكاليف التركيب و التكاليف الإضافية الأخرى.
          </p>
        </Title>
        <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key)}>
          {" "}
          <TabPane tab="مدخلات النظام" key="1">
            <div style={{ marginBottom: 2 }}>
              <Text>:احتياج الطاقة في النهار (أمبير)</Text>
              <Input
                type="number"
                placeholder="يرجى إدخال استهلاك الطاقة خلال فترة النهار بالأمبير."
                value={dayEnergyAmps}
                onChange={(e) =>
                  setDayEnergyAmps(e.target.value ? Number(e.target.value) : "")
                }
                style={{ marginTop: 8 }}
              />
              {/* <Text>:ساعات الاستخدام النهارية</Text>
              <Slider
                min={1}
                max={12}
                step={1}
                value={dayHours}
                onChange={(value) => setDayHours(value)}
                style={{ marginTop: 8 }}
              />
              <Text> {dayHours} ساعة </Text> */}
            </div>

            <div style={{ marginBottom: 20 }}>
              <Text> :احتياج الطاقة في الليل (أمبير)</Text>
              <Input
                type="number"
                placeholder="يرجى إدخال استهلاك الطاقة خلال فترة الليل بالأمبير."
                value={nightEnergyAmps}
                min={0}
                inputMode="numeric"
                aria-label="Night energy requirement in Amps"
                onChange={(e) =>
                  setNightEnergyAmps(
                    e.target.value ? Number(e.target.value) : ""
                  )
                }
                style={{ marginTop: 8 }}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <Text>ساعات الاستخدام الليلية:</Text>
              <Slider
                min={1}
                max={12}
                step={1}
                value={nightHours}
                onChange={(value) => setNightHours(value)}
                style={{ marginTop: 8 }}
                aria-valuemin={1}
                aria-valuemax={12}
                aria-valuenow={nightHours}
                aria-label="Night usage hours"
              />
              <Text>{nightHours} ساعة</Text>
            </div>

            <div style={{ marginBottom: 20 }}>
              {/* <Text>عدد ساعات سطوع الشمس في اليوم</Text>
              <Slider
                min={1}
                max={10}
                step={0.5}
                value={sunHours}
                onChange={(value) => setSunHours(value)}
                style={{ marginTop: 8 }}
              />
              <Text>{sunHours} hours</Text> */}
            </div>

            <div
              style={{
                marginBottom: 20,
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}>
              <div
                style={{
                  margin: 5,
                }}>
                <Text>نوع البطارية</Text>
                <Select
                  value={batteryType}
                  onChange={(value) => setBatteryType(value)}
                  style={{ width: "100%", marginTop: 8 }}>
                  {(batteries ?? []).map((battery) => (
                    <Option
                      key={battery.batteryType}
                      value={battery.batteryType}>
                      {battery.batteryType} {battery.batteryVoltage}V
                    </Option>
                  ))}
                </Select>
              </div>
              <div
                style={{
                  margin: 5,
                }}>
                <Text>نوع النظام</Text>
                <Select
                  value={systemType}
                  onChange={(value) => setSystemType(value)}
                  style={{ width: "100%", marginTop: 8 }}>
                  <Option value="hybrid">مع الشبكة</Option>
                  <Option value="offGrid">مستقل</Option>
                </Select>
              </div>
              <div style={{ margin: 5 }}>
                <Text>نوع الطور الكهربائي (Phase)</Text>
                <Select
                  value={phaseType}
                  onChange={(value) => setPhaseType(value)}
                  style={{ width: "100%", marginTop: 8 }}>
                  <Option value="single">1 Phase</Option>
                  <Option value="three">3 Phase</Option>
                </Select>
              </div>
            </div>

            <Button
              type="primary"
              block
              onClick={calculateSystem}
              disabled={
                typeof dayEnergyAmps !== "number" ||
                dayEnergyAmps <= 0 ||
                typeof nightEnergyAmps !== "number" ||
                nightEnergyAmps <= 0
              }>
              حساب نظام الطاقة الشمسية
            </Button>
          </TabPane>
          <TabPane key={2} tab="النتائج" disabled={!result}>
            {result && (
              <div style={{ marginTop: 20 }}>
                <Row gutter={[16, 16]}>
                  {result.panelResults.map((panelResult, index) => (
                    <Col span={12} key={index}>
                      <Card>
                        <Title level={5}>{panelResult.panelName}</Title>
                        <Text>Panels Needed: {panelResult.panelCount}</Text>
                        <br />
                        {/* <Text>Panel Cost: IQD {panelResult.panelCost}</Text> */}
                      </Card>
                    </Col>
                  ))}
                  <Col span={12}>
                    <Card>
                      <Title level={5}>Battery Storage</Title>
                      <Text>Batteries Needed: {result.batteryCount}</Text>
                      <br />
                      {/* <Text>Battery Cost: IQD {result.batteryCost}</Text> */}
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card>
                      <Title level={5}>Inverter</Title>
                      <Text>Inverter Size: {result.inverterSizeKw} kW</Text>
                      <br />
                      {/* <Text>Inverter Cost: IQD {result.inverterCost}</Text> */}
                    </Card>
                  </Col>

                  <Col span={12}>
                    <Card>
                      <Title level={5}>Total Cost</Title>
                      <Text>Total Cost: IQD {result.totalCost}</Text>
                    </Card>
                  </Col>
                </Row>
              </div>
            )}
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default CalculatorPage;
